import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../../contexts/APIContext";
import LikedContentCard from "../../components/cards/LikedContentCard";
import logger from "../../utils/logger";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

/**
 * Liked Shows — list of liked Storybooks and Video Stories.
 * GET /likes, then fetches details: storybook → getAudiobook, video → getStory (stories table).
 */
const LikedShows = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!apiClient) {
      setLoading(false);
      return;
    }

    const fetchLiked = async () => {
      setLoading(true);
      try {
        const response = await apiClient.getLikes();
        // Backend: { status: "success", data: [ { content_type, content_id } ] }
        // Support both response.data.data (wrapped) and response.data (direct array)
        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        console.log("❤️ getLikes full response", response);
        console.log("❤️ parsed likes list", list);

        const combined = [];

        for (const like of list) {
          const { content_type: contentType, content_id: contentId } = like;
          if (!contentId) continue;

          console.log("➡️ processing like", contentType, contentId);

          try {
            if (contentType === "storybook") {
              const res = await apiClient.getAudiobook(contentId);
              if (res?.status && res?.data) {
                const d = res.data;
                combined.push({
                  id: d.id ?? contentId,
                  type: "storybook",
                  title: d.title ?? "Untitled Storybook",
                  description: d.description ?? "",
                  thumbnail_url: d.thumbnail_url ?? d.cover_url ?? null,
                  views: d.views ?? 0,
                });
              }
            } else if (contentType === "video") {
              const res = await apiClient.getStory(contentId);
              if (res?.status && res?.data) {
                const d = res.data;
                combined.push({
                  id: d.id ?? contentId,
                  type: "video",
                  title: d.title ?? d.name ?? "Untitled Video",
                  description: d.description ?? "",
                  thumbnail_url: d.thumbnail_url ?? d.cover_image_url ?? null,
                  views: d.views ?? d.view_count ?? 0,
                });
              }
            }
          } catch (err) {
            logger.error("LikedShows: fetch detail failed", { contentType, contentId, err });
          }
        }

        console.log("✅ combined liked items", combined);
        setItems(combined);
      } catch (err) {
        logger.error("LikedShows: getLikes failed", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLiked();
  }, [apiClient]);

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1,
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -200, left: -180, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Header */}
        <div className="mb-8">
          <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Your Favorites
          </span>
          <h1 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: "clamp(1.75rem, 3.4vw, 2.4rem)", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1.05, margin: "6px 0 4px" }}>
            Liked <span style={{ color: "#0065F8" }}>Storybooks & Videos</span>
          </h1>
          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 15, margin: 0 }}>
            {loading
              ? "Loading the things you've hearted…"
              : items.length === 0
                ? "Tap the heart on any storybook or video and it'll show up here."
                : `${items.length} item${items.length !== 1 ? "s" : ""} you've saved.`}
          </p>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="aspect-[3/4] animate-pulse"
                style={{ background: "rgba(11,26,51,0.06)", borderRadius: 20 }}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div
            className="text-center mx-auto"
            style={{
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 24,
              padding: "56px 32px",
              maxWidth: 520,
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.06)",
            }}
          >
            <div
              className="mx-auto mb-5 flex items-center justify-center"
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(2,132,199,0.18) 0%, rgba(2,132,199,0.06) 100%)",
              }}
            >
              <svg width="40" height="40" fill="#0369A1" viewBox="0 0 24 24">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
              </svg>
            </div>
            <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 6px" }}>
              No favorites yet
            </h3>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 auto 18px", maxWidth: 320 }}>
              Tap the heart on any storybook or video and it'll show up here.
            </p>
            <button
              onClick={() => navigate("/storyverse")}
              className="transition active:scale-[0.97]"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: "#0065F8",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 14,
                border: "none",
                borderRadius: 999,
                padding: "12px 22px",
                boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                cursor: "pointer",
              }}
            >
              Explore Storyverse
            </button>
          </div>
        )}

        {/* Grid */}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {items.map((item) => (
              <LikedContentCard
                key={`${item.type}-${item.id}`}
                id={item.id}
                type={item.type}
                title={item.title}
                description={item.description}
                thumbnail={item.thumbnail_url}
                views={item.views}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LikedShows;
